export const Addon = (json: IDietAddon): IDietAddonModel => {
	const { name, description, status, active_to_order_from, active_to_order_to, addon_category, media, ...rest } = json

	return {
		...rest,
		name: LangContentToModel(name),
		description: LangContentToModel(description),
		category: getAddonCategorySlug(addon_category) as EAddonsCategoryTypes,
		status,
		activeToOrderFrom: active_to_order_from ? new Date(active_to_order_from) : null,
		activeToOrderTo: active_to_order_to ? new Date(active_to_order_to) : null,
		media: media ? media.map(m => Media(m)) : [],
	}
}

export const AddonMeal = (json: IDietAddonMeal): IDietAddonMealModel => DietMeal(json)

export const AddonDetails = (json: IDietAddonDetails): IDietAddonDetailsModel => {
	const {
		name,
		short_name,
		status,
		addon_category,
		min_order_qty,
		max_order_qty,
		active_to_order_from,
		active_to_order_to,
		description,
		recipe,
		filter_tags,
		marketing_tags,
		allergens,
		media,
		code,
		height,
		width,
		length,
		weight,
		...rest
	} = json

	return {
		...rest,
		name: LangContentToModel(name),
		shortName: LangContentToModel(short_name),
		status,
		category: getAddonCategorySlug(addon_category) as EAddonsCategoryTypes,
		categoryId: addon_category?.id ?? null,
		activeToOrderFrom: active_to_order_from ? new Date(active_to_order_from) : null,
		activeToOrderTo: active_to_order_to ? new Date(active_to_order_to) : null,
		minOrderQty: min_order_qty,
		maxOrderQty: max_order_qty,
		description: LangContentToModel(description),
		recipe,
		media: media ? media.map(m => Media(m)) : [],
		filterTags: filter_tags ? filter_tags.map(tag => DietFilterTag(tag)) : [],
		marketingTags: marketing_tags ? marketing_tags.map(tag => DietMarketingTag(tag)) : [],
		allergens: allergens ? allergens.map(tag => DietAllergen(tag)) : [],
		code,
		height: height ?? null,
		width: width ?? null,
		length: length ?? null,
		weight: weight ?? null,
	}
}

export const addonDetailsToJSON = (model: IDietAddonDetailsModel): IDietAddonUpdatePayload => {
	const {
		name,
		shortName,
		status,
		categoryId,
		activeToOrderFrom,
		activeToOrderTo,
		minOrderQty,
		maxOrderQty,
		description,
		recipe,
		filterTags,
		marketingTags,
		allergens,
		id,
		prices,
		ingredients,
		code,
		height,
		width,
		length,
		weight,
	} = model

	return {
		id,
		status,
		prices,
		code,
		name: LangContentToJson(name),
		short_name: LangContentToJson(shortName),
		meal_addon_category_id: categoryId,
		active_to_order_from: activeToOrderFrom ?? null,
		active_to_order_to: activeToOrderTo ?? null,
		min_order_qty: minOrderQty === 0 ? null : minOrderQty,
		max_order_qty: maxOrderQty === 0 ? null : maxOrderQty,
		description: LangContentToJson(description),
		recipe: recipe ?? undefined,
		ingredients: ingredients ?? undefined,
		filter_tags: filterTags.map(tag => tag.id),
		marketing_tags: marketingTags.map(tag => tag.id),
		allergens: allergens.map(allergen => allergen.id),
		height: height ?? null,
		width: width ?? null,
		length: length ?? null,
		weight: weight ?? null,
	}
}

/**
 * Payloads
 */
const resolveMealAddonCategoryId = (
	slug: EAddonsCategoryTypes | undefined,
	idBySlug: Record<string, number>,
): number | null => (slug ? (idBySlug[slug] ?? null) : null)

const DietAddonSharedToPayload = (
	event: IFormSubmitEvent<IDietAddonDialogForm>,
	others: IDietAddonPayloadOthers,
): IDietAddonSharedPayload => {
	const {
		minOrderQty,
		maxOrderQty,
		category,
		isDateRange,
		activeToOrderFrom,
		activeToOrderTo,
		ingredients,
		recipe,
		code,
		height,
		width,
		length,
		weight,
	} = event.values

	const { filterTags, marketingTags, allergens, addonCategoryIdBySlug } = others

	if (activeToOrderFrom) activeToOrderFrom.setHours(23, 59, 59, 999)
	if (activeToOrderTo) activeToOrderTo.setHours(23, 59, 59, 999)

	// Default dates for new addons (inactive by default -- both set to yesterday so today > toDate)
	const yesterday: Date = new Date()
	yesterday.setDate(yesterday.getDate() - 1)
	yesterday.setHours(23, 59, 59, 999)

	const finalFromDate: Date = isDateRange && activeToOrderFrom ? activeToOrderFrom : new Date(yesterday)
	const finalToDate: Date = isDateRange && activeToOrderTo ? activeToOrderTo : yesterday

	return {
		allergens,
		code,
		min_order_qty: minOrderQty === 0 ? null : minOrderQty,
		max_order_qty: maxOrderQty ?? null,
		filter_tags: filterTags,
		marketing_tags: marketingTags,
		prices: [],
		recipe: recipe ?? undefined,
		active_to_order_from: finalFromDate,
		active_to_order_to: finalToDate,
		meal_addon_category_id: resolveMealAddonCategoryId(category, addonCategoryIdBySlug),
		ingredients: ingredients ?? undefined,
		status: EDietStatus.ACTIVE,
		height: height ?? null,
		width: width ?? null,
		length: length ?? null,
		weight: weight ?? null,
	}
}

export const DietAddonUpdateToPayload = (
	updated: IDietAddonDetailsModel,
	event: IFormSubmitEvent<IDietAddonDialogForm>,
	others: IDietAddonPayloadOthers,
): IDietAddonUpdatePayload => {
	const { name, shortName, description } = event.values

	let _description: ReturnType<typeof LangContentToJson> | ILangContent[] | undefined
	if (updated.description) _description = LangContentToJson(updated.description, description)
	else if (description) _description = generateLangContentArray(description)

	const shared = DietAddonSharedToPayload(event, others)

	return {
		...shared,
		id: updated.id,
		name: LangContentToJson(updated.name, name),
		short_name: LangContentToJson(updated.shortName, shortName),
		description: _description ?? null,
		prices: updated.prices ?? [],
	}
}

export const DietAddonCreateToPayload = (
	event: IFormSubmitEvent<IDietAddonDialogForm>,
	others: IDietAddonPayloadOthers,
): IDietAddonCreatePayload => {
	const { name, shortName, description } = event.values

	const shared = DietAddonSharedToPayload(event, others)

	return {
		...shared,
		name: generateLangContentArray(name),
		short_name: generateLangContentArray(shortName),
		description: generateLangContentArray(description),
	}
}
